import { OnboardingTarget } from "../onboarding/onboardingTargets";
import ContextualHint, { HintId } from "../onboarding/hints/ContextualHint";
import LayersOutlinedIcon from "@mui/icons-material/LayersOutlined";
import PinDropOutlinedIcon from "@mui/icons-material/PinDropOutlined";
import TouchAppOutlinedIcon from "@mui/icons-material/TouchAppOutlined";
import { useDroppable } from "@dnd-kit/core";
import {
  SortableContext,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { MouseEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  selectIsTeleportAllowed,
  selectSelectedElementId,
  selectSelectedInteractionIds,
  selectSelectedTake,
  selectTakes360,
  setPrimaryInteraction,
  setSelectedInteraction,
  setSelectedTake,
} from "../../app/slices/TrainingSlice";
import Take360 from "../../models/Take360";
import EditorColumn, {
  EditorColumnInlineAddButton,
  EditorColumnEmptyState,
  EditorColumnHeader,
  EditorColumnRow,
  EditorColumnSection,
} from "../editor_columns/EditorColumn";
import EditorInteractionRow from "../editor_columns/EditorInteractionRow";
import InteractionContextMenu from "../interaction_context_menu/InteractionContextMenu";
import InteractionPicker from "../interaction_picker/InteractionPicker";
import {
  getInteractionDndId,
  getInteractionListDndId,
  TakeInteractionDndData,
  useTakeInteractionDndContext,
} from "./TakeInteractionDndProvider";

function Take360TeleportMarkerRow(props: { take: Take360 }) {
  const { take } = props;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const selectedElementId = useSelector(selectSelectedElementId);
  const takes = useSelector(selectTakes360);
  const marker = take.teleportMarker;
  const isAllowed = selectIsTeleportAllowed(takes, marker.id);

  return (
    <EditorColumnRow
      variant="flat"
      icon={<PinDropOutlinedIcon fontSize="inherit" />}
      title={t("teleport_marker")}
      titleClassName="font-medium"
      subtitle={isAllowed ? undefined : "Unavailable"}
      selected={selectedElementId === marker.id}
      disabled={!isAllowed}
      onClick={() => dispatch(setSelectedInteraction(marker.id))}
      testId="take360-teleport-marker-row"
    />
  );
}

export default function Take360InteractionColumn() {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const take = useSelector(selectSelectedTake);
  const selectedInteractionIds = useSelector(selectSelectedInteractionIds);
  const [isInteractionPickerOpen, setIsInteractionPickerOpen] = useState(false);
  const { hoverTakeId } = useTakeInteractionDndContext();
  const isHoverDropTarget = !!take && hoverTakeId === take.id;
  const droppableTakeId = take?.id ?? "take360-interactions";
  const { setNodeRef } = useDroppable({
    id: getInteractionListDndId(droppableTakeId),
    data: take
      ? ({
          kind: "interaction-list",
          takeId: take.id,
        } as TakeInteractionDndData)
      : undefined,
  });

  const openInteractionPicker = () => {
    if (!take) return;
    dispatch(setSelectedTake(take.id));
    setIsInteractionPickerOpen(true);
  };

  const handleContextMenu = (event: MouseEvent<HTMLDivElement>) => {
    if (!take) return;

    const row = (event.target as HTMLElement).closest<HTMLElement>(
      "[data-interaction-id]"
    );
    const interactionId = row?.dataset.interactionId;
    if (interactionId) {
      // a row inside the multi-selection keeps the group intact
      dispatch(
        selectedInteractionIds.includes(interactionId)
          ? setPrimaryInteraction(interactionId)
          : setSelectedInteraction(interactionId)
      );
      return;
    }

    dispatch(setSelectedTake(take.id));
  };

  return (
    <EditorColumn
      testId="take360-interaction-column"
      onboardingId={OnboardingTarget.Editor360InteractionColumn}
      widthClassName="w-full min-w-0"
      header={
        <EditorColumnHeader
          icon={<LayersOutlinedIcon fontSize="inherit" />}
          title={take?.name ?? t("onboarding.empty.no_bubble_selected")}
          testId="take360-interaction-column-header"
        />
      }
    >
      {!take ? (
        <EditorColumnEmptyState icon={<LayersOutlinedIcon />}>
          {t("onboarding.empty.select_take")}
        </EditorColumnEmptyState>
      ) : (
        <InteractionContextMenu>
          <div className="space-y-1.5" onContextMenu={handleContextMenu}>
            <EditorColumnSection
              title="Interactions"
              icon={<TouchAppOutlinedIcon fontSize="inherit" />}
              defaultExpanded
              testId="take360-interactions-section"
            >
              <SortableContext
                items={take.interactions.map((interaction) =>
                  getInteractionDndId(interaction.id)
                )}
                strategy={verticalListSortingStrategy}
                id={take.id}
              >
                <div
                  ref={setNodeRef}
                  className={`min-h-[34px] space-y-0.5 rounded-5 ${
                    isHoverDropTarget
                      ? "ring-1 ring-creator-primary bg-creator-primary-washed"
                      : ""
                  }`}
                  data-drop-target={isHoverDropTarget ? "true" : undefined}
                >
                  {take.interactions.length > 0 ? (
                    take.interactions.map((interaction) => (
                    <EditorInteractionRow
                      key={interaction.id}
                      interaction={interaction}
                      takeId={take.id}
                      testIdPrefix="take360-interaction-row"
                    />
                    ))
                  ) : (
                    <div className="flex flex-col gap-2">
                      <div className="px-2 py-2 text-[13px] font-normal italic text-creator-text-sub">
                        {t("onboarding.empty.no_interactions")}
                      </div>
                      <ContextualHint hintId={HintId.RightClickAdd} />
                    </div>
                  )}
                </div>
              </SortableContext>
              <ContextualHint
                hintId={HintId.MultiSelect}
                when={take.interactions.length > 3}
                className="mt-1.5"
              />
              <EditorColumnInlineAddButton
                label={t("add_interaction")}
                onClick={openInteractionPicker}
                disabled={!take}
                id="addInteractionButton-last"
                testId="take360-add-interaction"
                onboardingId={OnboardingTarget.Editor360AddInteraction}
              />
              <div className="pt-1.5">
                <div className="mx-1.5 mb-1.5 border-t border-creator-border" />
                <Take360TeleportMarkerRow take={take} />
              </div>
            </EditorColumnSection>
          </div>
        </InteractionContextMenu>
      )}
      <InteractionPicker
        open={isInteractionPickerOpen}
        onClose={() => setIsInteractionPickerOpen(false)}
      />
    </EditorColumn>
  );
}
